import { Pencil, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatDate } from '../../format-date';
import { AddressLink, PhoneLink } from './AddressLink';
import { removeName, type Supplier, type WriteOutcome } from './api';
import { Card } from './Card';
import { addressLine } from './maps';
import { IdentifierCheckBadge } from './SupplierMarks';
import { SupplierTypeList } from '../types';

/** A country's name in the reader's language; the code when the runtime does not know it. */
export function countryName(code: string | null | undefined, language: string): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * «Dati» (board 3.1): the registry, read; «Modifica» opens the form for whoever may edit it. What
 * is missing reads «non indicata» rather than vanishing, so «da completare» shows what to add.
 */
export function DataCard({
  supplier,
  onEdit,
  onWrite,
}: {
  readonly supplier: Supplier;
  /** Absent: no «Modifica» (no `can.edit`, a merged supplier). */
  readonly onEdit?: () => void;
  /** Absent: the other names cannot be taken away here (the side panel on a form). */
  readonly onWrite?: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t, i18n } = useTranslation('suppliers');
  const language = i18n.resolvedLanguage ?? 'it';
  const missing = <span className="text-muted-foreground">{t('data.missing')}</span>;
  const vat = supplier.vat
    ? `${supplier.vatCountry ?? supplier.country ?? ''} ${supplier.vat}`
    : null;

  return (
    <Card
      title={t('data.title')}
      action={
        onEdit ? (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil aria-hidden="true" />
            {t('actions.edit')}
          </Button>
        ) : null
      }
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @min-[620px]/shell:grid-cols-[minmax(9rem,auto)_1fr]">
        <Row label={t('data.legalName')}>{supplier.legalName ?? missing}</Row>
        <Row label={t('types.title')}>
          <SupplierTypeList types={supplier.types} />
        </Row>
        {supplier.otherNames.length > 0 ? (
          <Row label={t('data.otherNames')}>
            <OtherNames supplier={supplier} onWrite={onWrite} />
          </Row>
        ) : null}
        <Row label={t('data.country')}>{countryName(supplier.country, language) ?? missing}</Row>
        <Row label={t('data.vat')}>
          {vat === null ? (
            missing
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <span className="tabular-nums">{vat}</span>
              <IdentifierCheckBadge check={supplier.identifierCheck} />
              {supplier.vatCheckedAt ? (
                <span className="text-muted-foreground text-xs">
                  {t('data.checkedOn', { date: formatDate(supplier.vatCheckedAt) })}
                </span>
              ) : null}
            </span>
          )}
        </Row>
        {supplier.vatCheckName ? (
          <Row label={t('data.registerName')}>
            {supplier.vatCheckName}
            {supplier.vatCheckAddress ? (
              <span className="text-muted-foreground block text-xs">
                {supplier.vatCheckAddress}
              </span>
            ) : null}
          </Row>
        ) : null}
        {supplier.country === 'IT' || supplier.fiscalCode ? (
          <Row label={t('data.fiscalCode')}>{supplier.fiscalCode ?? missing}</Row>
        ) : null}
        <Row label={t('data.registeredOffice')}>
          {addressLine(supplier.registeredOffice?.address) === null ? (
            missing
          ) : (
            <AddressLink address={supplier.registeredOffice?.address} />
          )}
        </Row>
        {supplier.phone ? (
          <Row label={t('data.phone')}>
            <PhoneLink phone={supplier.phone} />
          </Row>
        ) : null}
        {supplier.usualCurrency ? (
          <Row label={t('data.usualCurrency')}>{supplier.usualCurrency}</Row>
        ) : null}
        {supplier.foreignTaxRegime ? (
          <Row label={t('data.foreignTaxRegime')}>{supplier.foreignTaxRegime}</Row>
        ) : null}
      </dl>
    </Card>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground text-xs font-semibold @min-[620px]/shell:pt-0.5">
        {label}
      </dt>
      <dd className="min-w-0 text-sm">{children}</dd>
    </>
  );
}

/** The other names as chips; Gestione and the approvers take one away with its ×. */
function OtherNames({
  supplier,
  onWrite,
}: {
  readonly supplier: Supplier;
  readonly onWrite: ((outcome: WriteOutcome, done: string) => void) | undefined;
}) {
  const { t } = useTranslation('suppliers');
  const [busy, setBusy] = useState<string | null>(null);

  async function remove(id: string, name: string) {
    if (busy !== null) return;
    setBusy(id);
    const outcome = await removeName(supplier.id, id);
    setBusy(null);
    onWrite?.(outcome, t('names.removed', { name }));
  }

  return (
    <ul className="flex flex-wrap gap-1.5">
      {supplier.otherNames.map((other) => (
        <li
          key={other.id}
          className="bg-muted text-foreground inline-flex items-center gap-1 rounded-full py-0.5 pr-1 pl-2.5 text-[12.5px] font-semibold"
        >
          {other.name}
          {supplier.can.removeName && onWrite ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-my-3 size-11"
              disabled={busy !== null}
              aria-label={t('names.remove', { name: other.name })}
              onClick={() => void remove(other.id, other.name)}
            >
              <X aria-hidden="true" />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
